import { customColors } from 'constants/colors'
import { email, Location, locations, phoneNr } from 'constants/contactData'
import styled from 'styled-components'
import {
  MdCall,
  MdLocationOn,
  MdMail,
  MdTrain,
  MdOutlineCheck,
  MdCalendarToday,
} from 'react-icons/md'
import { AiOutlineForm } from 'react-icons/ai'
import { useState } from 'react'
import { DESKTOP_STYLE, MOBILE_STYLE, sleep } from 'constants/variables'

export const ContactComponent = (props: {
  setDisplayContactForm: (v: boolean) => void
}) => {
  const [copied, setCopied] = useState(false)

  const handleClick = async () => {
    navigator.clipboard.writeText(`${phoneNr}`)
    setCopied(true)
    await sleep(3000)
    setCopied(false)
  }

  return (
    <ContactWrapper>
      <Card>
        <CardTitle>Kontakt</CardTitle>
        <InfoRow as="button" type="button" onClick={() => handleClick()}>
          <MdCall size={20} color={customColors.pastelGreen} />
          <span>{phoneNr}</span>
          {copied && (
            <Copied>
              kopiert
              <MdOutlineCheck color={customColors.pastelPurple} size={14} />
            </Copied>
          )}
        </InfoRow>
        <InfoRow as="a" href={`mailto:${email}`}>
          <MdMail size={20} color={customColors.pastelBlue} />
          <span>{email}</span>
        </InfoRow>
        <InfoRow
          as="button"
          type="button"
          onClick={() => props.setDisplayContactForm(true)}
        >
          <AiOutlineForm size={20} color={customColors.pastelPurple} />
          <span>Kontaktformular</span>
        </InfoRow>
      </Card>

      <Card>
        <CardTitle>Standorte</CardTitle>
        <LocationsGrid>
          {locations.map((location) => (
            <LocationBlock key={location.id} location={location} />
          ))}
        </LocationsGrid>
      </Card>
    </ContactWrapper>
  )
}

const LocationBlock = (props: { location: Location }) => {
  const { location } = props
  const isMain = location.role === 'hauptstandort'

  return (
    <LocationColumn>
      <LocationName $primary={isMain}>{location.name}</LocationName>
      <InfoRow as="a" href={location.mapsUrl} target="_blank" rel="noreferrer">
        <MdLocationOn
          size={20}
          color={
            isMain ? customColors.signaturePurple : customColors.pastelPurple
          }
        />
        <AddressText $primary={isMain}>{location.address}</AddressText>
      </InfoRow>
      {location.hint && location.hintUrl && (
        <InfoRow
          as="a"
          href={location.hintUrl}
          target="_blank"
          rel="noreferrer"
        >
          <MdTrain size={20} color={customColors.pastelPurple} />
          <span>{location.hint}</span>
        </InfoRow>
      )}
      <BookingLink href={location.bookingUrl} target="_blank" rel="noreferrer">
        <MdCalendarToday size={18} color="white" />
        <span>{location.bookingLabel}</span>
      </BookingLink>
    </LocationColumn>
  )
}

const ContactWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${DESKTOP_STYLE} {
    flex: 0 0 400px;
    width: 400px;
  }

  ${MOBILE_STYLE} {
    width: 100%;
    padding: 0 16px 32px 16px;
  }
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 24px;
  margin-bottom: 24px;
  border-radius: 8px;
  border: 2px solid ${customColors.signaturePurpleTransparent};
  background-color: white;
`

const CardTitle = styled.h3`
  font-family: 'Quicksand', sans-serif;
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 20px;
`

const LocationsGrid = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const LocationColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  &:not(:last-child) {
    padding-bottom: 28px;
    margin-bottom: 28px;
    border-bottom: 1px solid ${customColors.signaturePurpleTransparent};
  }
`

const LocationName = styled.h4<{ $primary: boolean }>`
  font-family: 'Quicksand', sans-serif;
  font-size: ${(props) => (props.$primary ? '20px' : '17px')};
  font-weight: ${(props) => (props.$primary ? 700 : 500)};
  margin-bottom: 12px;
  line-height: 1.3;
  color: ${(props) =>
    props.$primary ? customColors.signaturePurple : '#373737'};
`

const AddressText = styled.span<{ $primary: boolean }>`
  font-weight: ${(props) => (props.$primary ? 700 : 400)};
  color: ${(props) =>
    props.$primary ? customColors.signaturePurple : 'inherit'};
`

const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  margin: 0;
  margin-bottom: 12px;
  padding: 0;
  border: 0;
  background: none;
  font-family: 'Quicksand', sans-serif;
  font-size: 15px;
  line-height: 1.4;
  text-align: left;
  color: inherit;
  cursor: pointer;

  &:last-child {
    margin-bottom: 0;
  }

  span {
    min-width: 0;
  }

  &:hover span {
    text-decoration: underline;
  }

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`

const Copied = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${customColors.pastelPurple};
  font-size: 13px;
  text-decoration: none !important;
`

const BookingLink = styled.a`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 16px;
  padding: 12px 16px;
  border-radius: 3px;
  font-family: 'Quicksand', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  color: white;
  background-color: ${customColors.signaturePurple};
  text-decoration: none;
  transition: 0.3s ease-in-out;

  span {
    white-space: normal;
  }

  :hover {
    text-decoration: none;
    opacity: 0.85;
  }

  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
`
